import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

/**
 * Mutation to immediately release a Twilio phone number.
 *
 * Requirements:
 * - Phone number must have outbound calling disabled
 * - Provider must be Twilio
 * - User must be an Admin
 *
 * This bypasses the normal 30-day wait period for releasing numbers.
 */
export const useReleasePhoneNumberMutation = ({
	onSuccess,
	onError,
}: {
	onError?: (error: Error) => void;
	onSuccess?: () => void;
} = {}) => {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: async ({ phoneNumberId }: { phoneNumberId: string }) => {
			const result = await userSession.webServiceHelper.callWebServiceAsync(
				`aida/userPhoneNumbers/${phoneNumberId}/release`,
				'POST'
			);

			if (!result.success) {
				throw new Error(result.systemMessage || 'Failed to release phone number');
			}

			return result;
		},
		onError,
		onSuccess,
	});
};
